import { createContext, useCallback, useContext, useState } from 'react';
import type { MessageDescriptor } from 'react-intl';
import type { SetURLSearchParams } from 'react-router';

import type {
  CardCollectionSortingOption,
  SearchRefinementInput,
} from '#src/__generated__/globalTypes.ts';
import { FilterTitle } from '#src/components/search/FilterTitle/index.tsx';
import type {
  GallerySortName,
  PrintablePlayerSortName,
} from '#src/contexts/config/index.ts';
import type { LazyQueryExecFunction } from '#src/hooks/graphql/useLazyQuery.ts';

type Refinement = SearchRefinementInput;
export type RefinementType = 'string' | 'numeric' | 'virtual' | 'menu';

type FacetValue = { count: number; value: string };

export type GQLCtx = {
  query: string;
  activeFacets: {
    [attribute: string]: string[];
  };
  search: LazyQueryExecFunction<any, any>;
  extractFacetValues: (data: any) => any[];
  setSearchParams: SetURLSearchParams;
  refinements: Refinement[];
  advancedFilters: string;
  onSaleOnly: boolean;
  page: number;
  pages: number;
  nbHits: number;
  facets: {
    [field: string]: {
      values: FacetValue[];
      max?: { value: string };
      min?: { value: string };
    };
  };
  sorts:
    | PrintablePlayerSortName[]
    | GallerySortName[]
    | CardCollectionSortingOption[];
  urlToType: Record<string, RefinementType>;
  urlToApi: Record<string, string>;
};

export type GQLCtxBase = Pick<
  GQLCtx,
  | 'query'
  | 'page'
  | 'activeFacets'
  | 'refinements'
  | 'setSearchParams'
  | 'urlToApi'
  | 'urlToType'
>;

type ResponseFacet = {
  field: string;
  values: readonly { count: number; value: string; label?: string }[];
  min?: { value: string } | null;
  max?: { value: string } | null;
};

export const keyFacetsByApiField = (
  facets: readonly ResponseFacet[]
): GQLCtx['facets'] =>
  Object.fromEntries(
    facets.map(({ field, values, min, max }) => [
      field,
      { values: [...values], ...(min ? { min } : {}), ...(max ? { max } : {}) },
    ])
  );

export const GQLSearchContext = createContext<GQLCtx | null>(null);

export const useGQLSearchContext = () => {
  return useContext(GQLSearchContext);
};

export const toggle = (values: string[]) => (prev: string[]) => {
  const out = [...prev];
  for (const value of values) {
    const index = out.indexOf(value);
    if (index === -1) {
      out.push(value);
    } else {
      out.splice(index, 1);
    }
  }
  return out;
};

export const useFacet = (attribute: string) => {
  const ctx = useContext(GQLSearchContext)!;

  const value = ctx.activeFacets[attribute] ?? [];

  const apiAttribute = ctx.urlToApi[attribute];

  const [itemsFromSearch, setItemsFromSearch] = useState<any[] | null>(null);

  const items = (itemsFromSearch ?? ctx.facets[apiAttribute]?.values ?? []).map(
    e => ({
      ...e,
      label: e.value,
      isRefined: value.includes(e.value),
    })
  );

  return {
    search: (q: string) => {
      if (!q) {
        setItemsFromSearch(null);
        return;
      }
      ctx
        .search({
          variables: {
            refinements: ctx.refinements.filter(e => e.field !== apiAttribute),
            field: apiAttribute,
            facetQuery: q,
          },
        })
        .then(e => ctx.extractFacetValues(e.data))
        .then(setItemsFromSearch);
    },
    refine: (cb: (pv: string[]) => string[]) => {
      ctx.setSearchParams(searchParams => {
        const previousValues =
          searchParams.get(attribute)?.split(',').map(decodeURIComponent) ?? [];
        const nextValues = cb(previousValues);

        if (nextValues.length) {
          if (ctx.urlToType[attribute] === 'numeric') {
            searchParams.set(attribute, nextValues.join(','));
          } else {
            searchParams.set(
              attribute,
              nextValues.map(encodeURIComponent).join(',')
            );
          }
        } else {
          searchParams.delete(attribute);
        }
        return searchParams;
      });
    },
    value,
    isFromSearch: Boolean(itemsFromSearch),
    items,
    selected:
      ctx.urlToType[attribute] === 'numeric'
        ? Boolean(value.length)
        : value.length,
  };
};

export const useMenu = (attribute: string) => {
  const ctx = useContext(GQLSearchContext)!;

  const value = (ctx.activeFacets[attribute] ?? [])[0];

  const apiAttribute = ctx.urlToApi[attribute];
  const items = (ctx.facets[apiAttribute]?.values ?? []).map(e => ({
    ...e,
    label: e.value,
    isRefined: value === e.value,
  }));

  return {
    items,
    value,
    refine: (newValue: string) => {
      ctx.setSearchParams(searchParams => {
        if (searchParams.get(attribute) === newValue) {
          searchParams.delete(attribute);
        } else {
          searchParams.set(attribute, newValue);
        }
        return searchParams;
      });
    },
  };
};

export const useToggleChip = () => {
  const ctx = useContext(GQLSearchContext)!;
  return ({ value, attribute }: { value: string; attribute: string }) => {
    const type = ctx.urlToType[attribute];

    ctx.setSearchParams(searchParams => {
      // Range filters are all or nothing
      if (type === 'numeric') {
        searchParams.delete(attribute);
        return searchParams;
      }
      const previousValues =
        searchParams.get(attribute)?.split(',').map(decodeURIComponent) ?? [];
      const n = previousValues.filter(e => e !== value);
      if (n.length) {
        searchParams.set(attribute, n.map(encodeURIComponent).join(','));
      } else {
        searchParams.delete(attribute);
      }
      return searchParams;
    });
  };
};

type UseFiltersCountProps = {
  countQuery?: boolean;
  // TODO: implement sealed in gql context
  countSealed?: boolean;
  excludeFromCount?: string[];
};

export const useFiltersCount = ({
  countQuery = true,
  excludeFromCount,
}: UseFiltersCountProps = {}) => {
  const { query, activeFacets, urlToType } = useContext(GQLSearchContext)!;

  const filtersCount =
    (countQuery && query ? 1 : 0) +
    Object.entries(activeFacets).filter(
      ([k, v]) =>
        k !== 'ff' &&
        v.length &&
        !excludeFromCount?.includes(k) &&
        urlToType[k] !== 'menu'
    ).length;

  return filtersCount;
};

export const useClearAllFilters = () => {
  const { setSearchParams, activeFacets } = useContext(GQLSearchContext)!;
  return useCallback(() => {
    setSearchParams(searchParams => {
      // Delete query
      searchParams.delete('q');
      for (const key of Object.keys(activeFacets)) {
        searchParams.delete(key);
      }
      searchParams.delete('p');
      return searchParams;
    });
  }, [setSearchParams, activeFacets]);
};

export const useBackendPagination = () => {
  const ctx = useContext(GQLSearchContext)!;

  return {
    page: ctx.page,
    pages: ctx.pages,
    refine: (page: number) => {
      if (page === 0) {
        ctx.setSearchParams(searchParams => {
          searchParams.delete('p');
          return searchParams;
        });
      } else {
        ctx.setSearchParams(searchParams => {
          searchParams.set('p', (page + 1).toString());
          return searchParams;
        });
      }
    },
  };
};

const parseNum = (input: string | undefined) => {
  if (input != null) {
    return Number.parseInt(input, 10);
  }
  return undefined;
};

const format = (value: undefined | number, defaultV: undefined | number) => {
  if (value == null || value === defaultV) {
    return '';
  }
  return value.toString();
};

export const useBackendRangeSlider = ({
  attribute,
  min: minP,
  max: maxP,
}: {
  attribute: string;
  max?: number;
  min?: number;
}) => {
  const ctx = useContext(GQLSearchContext)!;

  const apiAttribute = ctx.urlToApi[attribute];

  const refine = useCallback(
    ([newMin, newMax]: [number | undefined, number | undefined]) => {
      const min =
        minP ??
        (parseNum(ctx.facets[apiAttribute]?.min?.value) as undefined | number);
      const max =
        maxP ??
        (parseNum(ctx.facets[apiAttribute]?.max?.value) as undefined | number);

      ctx.setSearchParams(prev => {
        if (!format(newMin, min).length && !format(newMax, max).length) {
          prev.delete(attribute);
        } else {
          prev.set(
            attribute,
            [format(newMin, min), format(newMax, max)].join(':')
          );
        }
        return prev;
      });
    },
    [ctx, attribute, apiAttribute, maxP, minP]
  );

  const min =
    minP ??
    (parseNum(ctx.facets[apiAttribute]?.min?.value) as undefined | number);
  const max =
    maxP ??
    (parseNum(ctx.facets[apiAttribute]?.max?.value) as undefined | number);

  const [start, end] =
    ctx.activeFacets[attribute]?.flatMap(e =>
      e
        .split(':')
        .map(rangeValue =>
          rangeValue === '' ? undefined : Number.parseInt(rangeValue, 10)
        )
    ) ?? [];

  const ret = {
    refine,
    range: { min, max },
    start: [start, end],
    canRefine: ctx.facets[apiAttribute] != null,
  };

  return ret;
};

type TitleProps = {
  name: string;
  shortTitle?: MessageDescriptor;
  title: MessageDescriptor;
  short?: boolean;
};
export const GqlFilterTitle = ({
  name,
  shortTitle,
  title,
  short,
}: TitleProps) => {
  const { selected } = useFacet(name);
  return (
    <FilterTitle name={(short && shortTitle) || title!} selected={selected} />
  );
};
